import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { SKILL_PRESETS } from "../../data/skillPresets";
import type { SkillDefinition } from "../../skills";

interface SkillPresetsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSkills: (skills: SkillDefinition[]) => void;
  existingSkillIds: Set<string>;
}

export function SkillPresetsPanel({ isOpen, onClose, onAddSkills, existingSkillIds }: SkillPresetsPanelProps) {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleSkill = (skillId: string) => {
    if (existingSkillIds.has(skillId)) return;
    setSelectedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
  };

  const handleAdd = () => {
    const skillsToAdd: SkillDefinition[] = [];
    for (const group of SKILL_PRESETS) {
      for (const skill of group.skills) {
        if (selectedSkills.has(skill.id)) {
          skillsToAdd.push(skill);
        }
      }
    }
    if (skillsToAdd.length > 0) {
      onAddSkills(skillsToAdd);
    }
    setSelectedSkills(new Set());
    onClose();
  };

  const handleClose = () => {
    setSelectedSkills(new Set());
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-lg flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-medium text-gray-900">{t('presets.addFromPreset')}</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">{t('presets.selectFromGroups')}</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {SKILL_PRESETS.map((group) => {
            const isExpanded = expandedGroups.has(group.id);
            const availableCount = group.skills.filter((s) => !existingSkillIds.has(s.id)).length;
            const selectedInGroup = group.skills.filter((s) => selectedSkills.has(s.id)).length;

            return (
              <div key={group.id} className="border border-gray-200 rounded">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{group.icon}</span>
                    <span className="text-xs font-medium text-gray-900">{group.name}</span>
                    {selectedInGroup > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                        {selectedInGroup}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">
                      {t('presets.available', { count: availableCount })}
                    </span>
                    <svg
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-2 space-y-0.5">
                    {group.skills.map((skill) => {
                      const exists = existingSkillIds.has(skill.id);
                      const isSelected = selectedSkills.has(skill.id);

                      return (
                        <label
                          key={skill.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                            exists
                              ? "opacity-50 cursor-not-allowed"
                              : isSelected
                              ? "bg-blue-50"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={exists}
                            onChange={() => toggleSkill(skill.id)}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className="text-sm">{skill.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-900">{skill.name}</div>
                            <div className="text-[10px] text-gray-500 truncate">{skill.description}</div>
                          </div>
                          {exists && (
                            <span className="text-[10px] text-gray-400 shrink-0">{t('presets.alreadyExists')}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <span className="text-[11px] text-gray-500">
            {selectedSkills.size > 0
              ? t('presets.selectedCount', { count: selectedSkills.size })
              : t('presets.noneSelected')}
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleAdd}
              disabled={selectedSkills.size === 0}
              className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('presets.addSelected')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
